import { useEffect, useState } from "react";
import { ArrowLeft, Loader2, CheckCircle2, CalendarX2 } from "lucide-react";
import api, { apiError } from "@/lib/api";

const fmtFull = new Intl.DateTimeFormat("fr-FR", { weekday: "long", day: "numeric", month: "long", year: "numeric" });

const inputCls =
  "w-full rounded-xl border border-[#E5DCD3] bg-[#FBF8F3] px-4 py-3 text-sm text-[#201815] placeholder:text-[#8A7971]/60 focus:border-[#C85A32] focus:outline-none transition-colors";

export default function Cancel() {
  const [form, setForm] = useState({ code: "", email: "" });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [found, setFound] = useState(null);
  const [done, setDone] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const lookup = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const { data } = await api.post("/reservations/cancel-lookup", form);
      setFound(data);
    } catch (err) {
      setError(apiError(err, "Recherche impossible pour le moment"));
    } finally {
      setLoading(false);
    }
  };

  const confirmCancel = async () => {
    setError("");
    setLoading(true);
    try {
      await api.post("/reservations/cancel", form);
      setDone(true);
    } catch (err) {
      setError(apiError(err, "Annulation impossible pour le moment"));
      setFound(null);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="cancel-page" className="min-h-screen bg-[#FBF8F3]">
      <div className="mx-auto max-w-xl px-4 sm:px-6 py-16 md:py-24">
        <a href="/" data-testid="cancel-back-link" className="inline-flex items-center gap-2 text-sm text-[#8A7971] hover:text-[#C85A32] transition-colors mb-10">
          <ArrowLeft size={15} /> Retour au site
        </a>

        <p className="text-xs font-medium uppercase tracking-[0.25em] text-[#C85A32] mb-3">Terre à Deux — Café céramique, Auch</p>
        <h1 className="font-display tracking-tight text-[#201815] text-3xl sm:text-4xl mb-4">Annuler une réservation</h1>

        {done ? (
          <div data-testid="cancel-success" className="rounded-2xl border border-[#4E7C59]/30 bg-[#4E7C59]/5 p-8 text-center">
            <CheckCircle2 size={42} className="mx-auto text-[#4E7C59] mb-4" />
            <p className="font-display text-2xl text-[#201815] mb-2">C'est annulé.</p>
            <p className="text-sm text-[#5C4E48] leading-relaxed">
              Votre réservation {found.code} est annulée et vos places sont à nouveau disponibles.
              Un email de confirmation vient de vous être envoyé. Au plaisir de vous accueillir une prochaine fois !
            </p>
            <a href="/#reservation" data-testid="cancel-rebook-link" className="mt-6 inline-block rounded-full bg-[#201815] px-7 py-3.5 text-sm font-medium text-[#FBF8F3] hover:bg-[#C85A32] transition-colors">
              Réserver un autre créneau
            </a>
          </div>
        ) : found ? (
          <div data-testid="cancel-confirm-card" className="rounded-2xl border border-[#E5DCD3] bg-white p-8">
            <p className="text-xs uppercase tracking-[0.25em] text-[#8A7971] mb-4">Votre réservation</p>
            <p className="font-display text-xl text-[#201815]">{found.name}</p>
            <p className="text-sm text-[#5C4E48] mt-2 leading-relaxed">
              {found.workshop_name} — {fmtFull.format(new Date(found.date + "T12:00:00"))} à {found.time},{" "}
              {found.seats} place{found.seats > 1 ? "s" : ""}.<br />
              Code <span className="font-mono-brand text-[#C85A32]">{found.code}</span>
            </p>
            <div className="mt-6 rounded-xl bg-[#C85A32]/5 border border-[#C85A32]/20 px-4 py-3 text-xs text-[#A84623] leading-relaxed">
              L'annulation est définitive et libère immédiatement vos places. Une confirmation vous sera envoyée par email.
            </div>
            {error && (
              <p data-testid="cancel-error" className="mt-4 rounded-xl border border-[#C85A32]/40 bg-[#C85A32]/10 px-4 py-3 text-sm text-[#A84623]">{error}</p>
            )}
            <div className="mt-6 flex flex-wrap gap-3">
              <button
                data-testid="cancel-confirm-btn"
                onClick={confirmCancel}
                disabled={loading}
                className="inline-flex items-center gap-2 rounded-full bg-[#C85A32] px-6 py-3 text-sm font-medium text-white hover:bg-[#A84623] transition-colors disabled:opacity-40"
              >
                {loading ? <Loader2 size={14} className="animate-spin" /> : <CalendarX2 size={14} />}
                Confirmer l'annulation
              </button>
              <button
                data-testid="cancel-back-form-btn"
                onClick={() => { setFound(null); setError(""); }}
                className="rounded-full border border-[#201815]/15 px-6 py-3 text-sm text-[#5C4E48] hover:border-[#C85A32] hover:text-[#C85A32] transition-colors"
              >
                Ce n'est pas ma réservation
              </button>
            </div>
          </div>
        ) : (
          <>
            <p className="text-sm text-[#5C4E48] leading-relaxed mb-8">
              Un empêchement ? Pas besoin d'appeler : saisissez le code de réservation reçu par email
              (format AC-XXXXXX) et l'adresse email utilisée lors de la réservation.
            </p>
            <form data-testid="cancel-form" onSubmit={lookup} className="rounded-2xl border border-[#E5DCD3] bg-white p-8 space-y-4">
              <div>
                <label className="block text-xs text-[#8A7971] mb-1.5">Code de réservation</label>
                <input
                  data-testid="cancel-code-input"
                  required
                  minLength={4}
                  placeholder="AC-XXXXXX"
                  value={form.code}
                  onChange={(e) => setForm({ ...form, code: e.target.value.toUpperCase() })}
                  className={`${inputCls} font-mono-brand tracking-widest`}
                />
              </div>
              <div>
                <label className="block text-xs text-[#8A7971] mb-1.5">Email de réservation</label>
                <input
                  data-testid="cancel-email-input"
                  required
                  type="email"
                  placeholder="votre@email.fr"
                  value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                  className={inputCls}
                />
              </div>
              {error && (
                <p data-testid="cancel-error" className="rounded-xl border border-[#C85A32]/40 bg-[#C85A32]/10 px-4 py-3 text-sm text-[#A84623]">{error}</p>
              )}
              <button
                data-testid="cancel-lookup-btn"
                type="submit"
                disabled={loading}
                className="w-full inline-flex items-center justify-center gap-2 rounded-full bg-[#201815] py-3.5 text-sm font-medium text-[#FBF8F3] hover:bg-[#C85A32] transition-colors disabled:opacity-40"
              >
                {loading && <Loader2 size={14} className="animate-spin" />}
                Retrouver ma réservation
              </button>
            </form>
            <p className="mt-6 text-xs text-[#8A7971] leading-relaxed">
              Vous ne retrouvez plus votre code ? Contactez-nous au 06 31 82 89 64 ou à terreadeux@gmail.com.
            </p>
          </>
        )}
      </div>
    </div>
  );
}
